import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  GraduationCap,
  Globe,
  Users,
  Lightbulb,
  Check,
  Megaphone,
  Plane,
  Award,
  BookOpen,
  Sparkles,
} from "lucide-react";
import { studyAbroad, brand } from "../mock";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingActions from "../components/FloatingActions";

const iconMap = { GraduationCap, Globe, Users, Lightbulb };

export default function StudyAbroad({ onEnroll }) {
  return (
    <div className="App">
      <Navbar onEnroll={onEnroll} />

      {/* HERO */}
      <section className="relative pt-28 md:pt-32 pb-16 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, #0A1740 0%, #1E1B4B 60%, #312E81 100%)",
          }}
        />
        <div className="absolute inset-0 opacity-25">
          <img src={studyAbroad.hero} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1740]/60 via-transparent to-[#0A1740]/95" />

        <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-amber-300 text-sm font-semibold">
            <ArrowLeft size={16} /> Back to Home
          </Link>

          <div className="mt-6 grid lg:grid-cols-2 gap-10 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/25 text-amber-300 text-xs font-black tracking-wider uppercase backdrop-blur">
                <Sparkles size={14} /> Global Education Program
              </div>
              <h1
                className="mt-5 text-5xl md:text-7xl font-black leading-[0.9]"
                style={{ fontFamily: "Bebas Neue, sans-serif" }}
              >
                STUDY IN
              </h1>
              <div
                className="text-6xl md:text-8xl text-amber-300 leading-[0.9]"
                style={{ fontFamily: "Caveat, cursive", fontWeight: 700 }}
              >
                South Korea
              </div>

              <div className="mt-5 inline-block bg-[#EC4899] text-white px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wide">
                {studyAbroad.tagline}
              </div>

              <div className="mt-8 flex items-start gap-4">
                <div className="w-16 h-16 shrink-0 rounded-full bg-white flex items-center justify-center">
                  <GraduationCap size={30} className="text-[#0A1740]" />
                </div>
                <div>
                  <div
                    className="text-3xl md:text-4xl font-black text-white"
                    style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
                  >
                    {studyAbroad.universityKr}
                  </div>
                  <div className="text-lg md:text-xl font-extrabold text-[#F472B6]">
                    {studyAbroad.university}
                  </div>
                </div>
              </div>

              <div className="mt-5 inline-flex items-center gap-2 text-white/85">
                <MapPin size={16} className="text-amber-300" />
                <span className="font-semibold text-sm">{studyAbroad.location.toUpperCase()}</span>
              </div>

              <div className="mt-6 rounded-2xl bg-[#0A1740] border border-white/10 px-5 py-3 inline-flex items-center gap-3">
                <span className="text-white font-bold text-sm md:text-base">Your gateway to a</span>
                <span className="text-amber-300 font-black text-sm md:text-base">BRIGHT GLOBAL CAREER</span>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={onEnroll} className="btn-primary px-6 py-3.5 rounded-full inline-flex items-center gap-2">
                  Apply Now <ArrowRight size={18} />
                </button>
                <a
                  href={brand.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="px-6 py-3.5 rounded-full border border-white/25 text-white hover:bg-white/10 transition-colors backdrop-blur inline-flex items-center gap-2"
                >
                  <Plane size={18} /> Free Counselling
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden border-4 border-white/20 shadow-2xl aspect-[4/5] max-w-md mx-auto">
                <img src={studyAbroad.campus} alt="Gimcheon University" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1740] via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="text-xs uppercase tracking-widest text-amber-300 font-bold">Since 1978</div>
                  <div className="text-xl font-extrabold">Modern Campus • Safe City</div>
                </div>
              </div>
            </div>
          </div>

          {/* Pillars */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {studyAbroad.pillars.map((p) => {
              const Icon = iconMap[p.icon] || GraduationCap;
              return (
                <div key={p.title} className="rounded-2xl bg-white/8 border border-white/15 backdrop-blur p-5">
                  <div className="w-11 h-11 rounded-xl bg-amber-400/20 border border-amber-300/40 flex items-center justify-center text-amber-300">
                    <Icon size={20} />
                  </div>
                  <div className="mt-4 text-white font-extrabold text-sm uppercase tracking-wide">{p.title}</div>
                  <div className="mt-1 text-white/70 text-xs leading-relaxed">{p.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Programs + Why Choose */}
      <section className="section-light py-16 md:py-24 relative overflow-hidden">
        <div className="blob blob-pink w-96 h-96 -top-10 -left-10" />
        <div className="blob blob-purple w-80 h-80 bottom-0 -right-10" />

        <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10 grid lg:grid-cols-2 gap-8">
          {/* Programs list */}
          <div className="rounded-3xl bg-white border border-[#0A1740]/10 shadow-lg overflow-hidden">
            <div
              className="px-6 py-4 flex items-center gap-3"
              style={{ background: "linear-gradient(90deg, #E11D48 0%, #EC4899 100%)" }}
            >
              <BookOpen className="text-white" size={22} />
              <h3 className="text-white font-extrabold text-lg tracking-wide">ALL COURSES AVAILABLE</h3>
            </div>
            <div className="p-5 space-y-4">
              {studyAbroad.programs.map((p) => (
                <div key={p.name} className="flex gap-4 items-start">
                  <div
                    className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center text-white shadow"
                    style={{ background: p.color }}
                  >
                    <BookOpen size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-extrabold text-[15px]" style={{ color: p.color }}>
                      {p.name.toUpperCase()}
                    </div>
                    <div className="mt-1 text-[13px] text-[#0A1740]/75 leading-relaxed">
                      {p.items.join(" • ")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Why choose + notice */}
          <div className="space-y-6">
            <div className="rounded-3xl bg-white border border-[#0A1740]/10 shadow-lg overflow-hidden">
              <div
                className="px-6 py-4 flex items-center gap-3"
                style={{ background: "linear-gradient(90deg, #0A1740 0%, #1E3A8A 100%)" }}
              >
                <Award className="text-amber-300" size={22} />
                <h3 className="text-white font-extrabold text-lg tracking-wide">
                  WHY CHOOSE <span className="text-amber-300">GIMCHEON UNIVERSITY?</span>
                </h3>
              </div>
              <ul className="p-6 space-y-3">
                {studyAbroad.whyChoose.map((w) => (
                  <li key={w} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#0A1740] flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={14} className="text-amber-300" strokeWidth={3} />
                    </div>
                    <span className="text-[#0A1740] text-sm md:text-[15px] font-medium">{w}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl bg-white border border-[#0A1740]/10 shadow-lg overflow-hidden">
              <div
                className="px-6 py-4 flex items-center gap-3"
                style={{ background: "linear-gradient(90deg, #E11D48 0%, #F97316 100%)" }}
              >
                <Megaphone className="text-white" size={22} />
                <h3 className="text-white font-extrabold text-lg tracking-wide">IMPORTANT NOTICE</h3>
              </div>
              <ul className="p-6 space-y-3">
                {studyAbroad.notices.map((n, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check size={16} className="text-[#E11D48] mt-1 shrink-0" strokeWidth={3} />
                    <span
                      className="text-[#0A1740] text-sm md:text-[15px]"
                      dangerouslySetInnerHTML={{
                        __html: n.replace(
                          /seats are limited\./,
                          '<span class="text-[#E11D48] font-extrabold">seats are limited.</span>'
                        ),
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div
            className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center"
            style={{
              background:
                "linear-gradient(135deg, #0A1740 0%, #1E1B4B 55%, #3B0764 100%)",
            }}
          >
            <div className="text-white">
              <div className="text-white text-xl md:text-2xl font-semibold" style={{ fontFamily: "Caveat, cursive" }}>
                Take the Next Step
              </div>
              <div
                className="text-4xl md:text-5xl text-amber-300 leading-tight"
                style={{ fontFamily: "Caveat, cursive", fontWeight: 700 }}
              >
                Towards Your Dream!
              </div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs md:text-sm">
                {[
                  { label: "Study Abroad", icon: Plane },
                  { label: "Experience New Culture", icon: Globe },
                  { label: "Build a Global Future", icon: BookOpen },
                ].map(({ label, icon: I }) => (
                  <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white border border-white/15">
                    <I size={14} className="text-amber-300" /> {label}
                  </span>
                ))}
              </div>
              <p className="mt-5 text-white/70 text-sm md:text-base">
                {studyAbroad.cta.subheading}
              </p>
            </div>

            <div className="flex justify-center">
              <button
                onClick={onEnroll}
                className="relative inline-flex flex-col items-center gap-1 bg-amber-400 hover:bg-amber-300 text-[#0A1740] rounded-3xl px-10 py-6 shadow-2xl border-4 border-white/20 transition-colors"
              >
                <GraduationCap size={36} strokeWidth={2.5} />
                <span className="text-3xl md:text-4xl font-black" style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: "0.05em" }}>
                  APPLY NOW
                </span>
                <span className="text-xs font-bold tracking-wider uppercase">for Gimcheon University</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}
